import useSWR from 'swr';
import { supabase } from '../lib/supabase';

// Type definition for featured analysis data structure
export interface FeaturedAnalysis {
  id: string;
  slug: string; // SEO友好的URL slug
  title: string;
  summary: string;
  category: string;
  template_name: string;
  preview_content: string;
  full_article: string; // 完整文章内容
  created_at: string;
}

// Configuration options for the hook
export interface UseFeaturedAnalysesOptions {
  limit?: number;
  refreshInterval?: number;
  revalidateOnFocus?: boolean;
  revalidateOnReconnect?: boolean;
  suspense?: boolean;
}

// Hook return type
export interface UseFeaturedAnalysesReturn {
  data: FeaturedAnalysis[] | undefined;
  error: Error | undefined;
  isLoading: boolean;
  isValidating: boolean;
  mutate: () => Promise<FeaturedAnalysis[] | undefined>;
  revalidate: () => Promise<FeaturedAnalysis[] | undefined>;
}

// Fetcher function for SWR
const fetchFeaturedAnalyses = async (url: string): Promise<FeaturedAnalysis[]> => {
  console.log('🚀 开始获取精选分析数据:', url);
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    console.log('🔧 环境变量检查:', {
      hasUrl: !!supabaseUrl,
      hasKey: !!supabaseAnonKey
    });
    
    if (!supabaseUrl) {
      throw new Error('Supabase URL not configured');
    }
    
    if (!supabaseAnonKey) {
      throw new Error('Supabase anon key not configured');
    }

    console.log('📡 发送请求到:', url);
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
    });

    console.log('📨 响应状态:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API错误响应:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const responseData = await response.json();
    console.log('✅ 成功获取数据:', responseData);
    
    // Handle standardized response format from admin-utils
    let data;
    if (responseData && typeof responseData === 'object' && 'success' in responseData) {
      // Standardized response format: { success: true, data: [...] }
      if (!responseData.success) {
        throw new Error(responseData.error || 'API request failed');
      }
      data = responseData.data;
    } else {
      // Direct array response (fallback for backward compatibility)
      data = responseData;
    }
    
    // Validate the actual data structure
    if (!Array.isArray(data)) {
      console.error('❌ 响应数据格式错误:', { responseData, extractedData: data });
      throw new Error('Invalid response format: expected array in data field');
    }

    console.log('✅ 解析后的数据数组:', data.length, '条记录');
    return data;
  } catch (error) {
    console.error('❌ 获取精选分析失败:', error);
    console.error('错误详情:', error instanceof Error ? error.message : error);
    throw error;
  }
};

/**
 * Custom hook for fetching featured analyses with SWR
 * 
 * Features:
 * - Automatic caching and revalidation
 * - Background updates
 * - Error handling
 * - Loading states
 * - Manual revalidation for CSR and SSR
 * - Configurable options
 * 
 * @param options Configuration options for the hook
 * @returns Hook state and methods
 */
export const useFeaturedAnalyses = (
  options: UseFeaturedAnalysesOptions = {}
): UseFeaturedAnalysesReturn => {
  console.log('🎣 useFeaturedAnalyses hook初始化', options);
  
  const {
    limit = 5,
    refreshInterval = 0, // 禁用自动刷新，减少不必要的请求
    revalidateOnFocus = false, // 禁用焦点时重新验证
    revalidateOnReconnect = true, // 保留网络重连时的重新验证
    suspense = false,
  } = options;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  console.log('🔗 Supabase URL:', supabaseUrl ? '已配置' : '未配置');
  
  // Construct the API URL
  const apiUrl = supabaseUrl 
    ? `${supabaseUrl}/functions/v1/public-get-official-news-analyes?limit=${limit}`
    : null;
    
  console.log('📡 API URL:', apiUrl);

  // Use SWR with the fetcher function
  const {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
  } = useSWR<FeaturedAnalysis[], Error>(
    apiUrl, // Key - will be null if supabaseUrl is not available
    fetchFeaturedAnalyses,
    {
      refreshInterval,
      revalidateOnFocus,
      revalidateOnReconnect,
      suspense,
      // 添加重试配置
      errorRetryCount: 2,
      errorRetryInterval: 2000,
      // 添加去重配置，防止重复请求
      dedupingInterval: 10000, // 10秒内相同请求去重
      onError: (error) => {
        console.error('SWR Error in useFeaturedAnalyses:', error);
      },
      onSuccess: (data) => {
        console.log('Successfully fetched featured analyses:', data?.length, 'items');
      },
      // Add fallback data structure
      fallbackData: undefined,
      // Keep previous data while revalidating
      keepPreviousData: true,
    }
  );

  // Manual revalidation function (useful for CSR and SSR)
  const revalidate = async (): Promise<FeaturedAnalysis[] | undefined> => {
    try {
      console.log('Manual revalidation triggered for featured analyses');
      const result = await mutate();
      return result;
    } catch (error) {
      console.error('Manual revalidation failed:', error);
      throw error;
    }
  };

  return {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
    revalidate,
  };
};

/**
 * Static function to prefetch featured analyses data (useful for SSR)
 * 
 * @param limit Number of analyses to fetch
 * @returns Promise resolving to the fetched data
 */
export const prefetchFeaturedAnalyses = async (
  limit: number = 5
): Promise<FeaturedAnalysis[]> => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  
  if (!supabaseUrl) {
    throw new Error('Supabase URL not configured');
  }

  const apiUrl = `${supabaseUrl}/functions/v1/public-get-official-news-analyes?limit=${limit}`;
  
  try {
    return await fetchFeaturedAnalyses(apiUrl);
  } catch (error) {
    console.error('Failed to prefetch featured analyses:', error);
    throw error;
  }
};

/**
 * Utility hook for getting a specific featured analysis by ID
 * This uses the same cache as the main hook but filters for a specific item
 * 
 * @param id The ID of the analysis to retrieve
 * @param options SWR options
 * @returns Single analysis or undefined
 */
export const useFeaturedAnalysis = (
  id: string,
  options: UseFeaturedAnalysesOptions = {}
) => {
  const { data, error, isLoading, isValidating, revalidate } = useFeaturedAnalyses(options);
  
  const analysis = data?.find(item => item.id === id);
  
  return {
    data: analysis,
    error,
    isLoading,
    isValidating,
    revalidate,
  };
};

/**
 * Utility hook for getting a specific featured analysis by slug (SEO-friendly)
 * This uses the same cache as the main hook but filters for a specific item by slug
 * 
 * @param slug The slug of the analysis to retrieve
 * @param options SWR options
 * @returns Single analysis or undefined
 */
export const useFeaturedAnalysisBySlug = (
  slug: string,
  options: UseFeaturedAnalysesOptions = {}
) => {
  const { data, error, isLoading, isValidating, revalidate } = useFeaturedAnalyses(options);
  
  const analysis = data?.find(item => item.slug === slug);
  
  return {
    data: analysis,
    error,
    isLoading,
    isValidating,
    revalidate,
  };
};

/**
 * Utility function to generate SEO-friendly slug from title
 * @param title The title to convert to slug
 * @returns SEO-friendly slug
 */
export const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // 移除特殊字符
    .replace(/\s+/g, '-') // 空格替换为连字符
    .replace(/-+/g, '-') // 多个连字符合并为一个
    .trim();
};

export default useFeaturedAnalyses;
